import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured
import Branding from './Branding';
import axios from 'axios';

// PostHog initialization disabled for FERPA compliance
// Will be replaced with self-hosted instance later
// See: https://posthog.com/docs/getting-started/install

const NewsletterLanding = () => {
  const [searchParams] = useSearchParams();
  const [selectedSegment, setSelectedSegment] = useState('parents');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectionStartTime, setSelectionStartTime] = useState(Date.now());
  
  // Extract UTM parameters
  const utmSource = searchParams.get('utm_source') || 'direct';
  const utmCampaign = searchParams.get('utm_campaign') || 'newsletter';
  const utmMedium = searchParams.get('utm_medium') || 'organic';

  // Infer expected audience from UTM parameters
  const inferAudienceFromCampaign = () => {
    const campaign = utmCampaign.toLowerCase();
    const source = utmSource.toLowerCase();
    
    if (campaign.includes('parent') || campaign.includes('child') || campaign.includes('homework')) return 'parents';
    if (campaign.includes('classroom') || campaign.includes('teacher') || campaign.includes('educator')) return 'educators';
    if (campaign.includes('district') || campaign.includes('admin') || campaign.includes('institutional')) return 'administrators';
    
    if (source === 'tiktok' || source === 'instagram' || source === 'facebook') return 'parents';
    if (source === 'linkedin') return 'educators'; // Could be admin too, but educators more likely
    
    return 'parents'; // Default fallback
  };

  const expectedAudience = inferAudienceFromCampaign();

  useEffect(() => {
    // Track landing page visit
    posthog.capture('newsletter_landing_visited', {
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      utm_medium: utmMedium,
      expected_audience: expectedAudience,
      page_url: window.location.href
    });

    // Set default segment based on campaign
    setSelectedSegment(expectedAudience);
  }, [utmSource, utmCampaign, utmMedium, expectedAudience]);

  // Handle redirect after successful signup
  useEffect(() => {
    if (showSuccess) {
      const redirectTimer = setTimeout(() => {
        window.location.href = 'https://teachtappy.ai';
      }, 2000);

      // Cleanup timer if component unmounts
      return () => clearTimeout(redirectTimer);
    }
  }, [showSuccess]);

  const handleSegmentSelection = (segment) => {
    const selectionTime = (Date.now() - selectionStartTime) / 1000;
    
    posthog.capture('newsletter_segment_selected', {
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      expected_segment: expectedAudience,
      selected_segment: segment,
      audience_match: expectedAudience === segment,
      selection_time_seconds: selectionTime
    });

    setSelectedSegment(segment);
    setSelectionStartTime(Date.now());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Submit to newsletter proxy endpoint (production-ready HTTPS)  
    try {
      // Use same-domain API proxy (production-ready HTTPS approach)
      await axios.post(`/api/newsletter/subscribe`, {
        email: email,
        segment: selectedSegment,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium
      });

      // Track successful signup
      posthog.capture('newsletter_subscribed', {
        segment: selectedSegment,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        expected_vs_actual: expectedAudience === selectedSegment ? 'match' : 'mismatch',
        signup_source: 'landing_page'
      });

      setShowSuccess(true);

      // Trigger immediate value delivery
      if (selectedSegment === 'parents') {
        // Download AI Safety Checklist
        window.open('/downloads/ai-safety-checklist.pdf', '_blank');
      } else if (selectedSegment === 'educators') {
        // Download Classroom AI Tools Matrix
        window.open('/downloads/classroom-ai-matrix.pdf', '_blank');
      } else {
        // Download Vendor Evaluation Scorecard
        window.open('/downloads/vendor-scorecard.pdf', '_blank');
      }

    } catch (error) {
      console.error('Newsletter signup failed:', error);
      
      // Detailed error tracking
      const errorDetails = {
        segment: selectedSegment,
        utm_source: utmSource,
        error_message: error.message,
        error_status: error.response?.status,
        endpoint_url: '/api/newsletter/subscribe'
      };
      
      posthog.capture('newsletter_signup_error', errorDetails);
      
      // User-friendly error messages
      let errorMessage = 'Signup failed. Please try again.';
      if (error.response?.status === 400) {
        errorMessage = 'Please check your email address and try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Our servers are busy. Please try again in a moment.';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Connection issue. Please check your internet and try again.';
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Segment-specific content
  const segmentContent = {
    parents: {
      hero: "Get AI Education Intelligence That Actually Helps Parents",
      subheading: "The weekly checklist that helps you evaluate AI tools for your child",
      value: "🔍 Weekly AI safety red flags every parent should know",
      social_proof: "Join 5,000+ parents making confident AI education decisions",
      immediate_download: "AI Safety Checklist for Parents",
      pain_point: "Don't let your child use AI tools you can't understand or trust"
    },
    educators: {
      hero: "Monday-Morning AI Strategies for Teachers",
      subheading: "Classroom-ready AI guidance you can use this week", 
      value: "📚 Weekly AI tool comparisons that actually save teaching time",
      social_proof: "Trusted by 3,000+ teachers nationwide",
      immediate_download: "Classroom AI Tools Effectiveness Matrix",
      pain_point: "Stop wasting time on AI tools that create more work, not less"
    },
    administrators: {
      hero: "AI Vendor Analysis for School Leaders",
      subheading: "ROI data and vendor analysis for confident institutional decisions",
      value: "📊 Weekly institutional AI insights with real district ROI data", 
      social_proof: "Used by 500+ school administrators",
      immediate_download: "AI Vendor Evaluation Scorecard",
      pain_point: "Make AI decisions you can confidently defend to boards and parents"
    }
  };

  const content = segmentContent[selectedSegment];

  if (showSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '1rem' }}>✅</div>
          <h1 style={{ 
            color: Branding.colors.primary.teal,
            marginBottom: '1rem',
            fontSize: '24px'
          }}>
            Welcome to the Newsletter!
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem', lineHeight: 1.6 }}>
            Check your email for your first newsletter and your free {content.immediate_download}.
          </p>
          <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ color: '#333', fontSize: '14px', margin: 0 }}>
              💡 <strong>Pro tip:</strong> Add us to your contacts so you never miss our insights!
            </p>
          </div>
          <button
            onClick={() => window.location.href = 'https://teachtappy.ai'}
            style={{
              background: 'linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Explore Tappy Labs →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FAFAFA 0%, #F1F5F9 100%)',
      padding: '2rem 1rem'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <img 
          src="/images/TAPPY-logo-color-1.png" 
          alt="Tappy Labs" 
          style={{ height: '48px', marginBottom: '2rem' }}
        />
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)'
      }}>
        
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)',
          color: 'white',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            marginBottom: '1rem',
            lineHeight: 1.2
          }}>
            {content.hero}
          </h1>
          <p style={{ 
            fontSize: '18px', 
            opacity: 1, 
            marginBottom: '2rem',
            lineHeight: 1.5,
            fontWeight: '400'
          }}>
            {content.subheading}
          </p>
          <div style={{
            background: 'rgba(255, 255, 255, 0.15)',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1rem',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '16px',
              fontWeight: '500',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}>
              {content.value}
            </p>
          </div>
          <p style={{ 
            fontSize: '14px', 
            opacity: 0.95, 
            margin: 0,
            fontWeight: '400',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
          }}>
            {content.social_proof}
          </p>
        </div>

        {/* Segment Selection */}
        <div style={{ padding: '2rem' }}>
          <h3 style={{ 
            textAlign: 'center', 
            marginBottom: '1.5rem',
            color: '#333',
            fontSize: '20px'
          }}>
            I want help with...
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gap: '1rem', 
            marginBottom: '2rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
          }}>
            {[
              { key: 'parents', icon: '📚', label: 'My child\'s AI education', desc: 'Get parent-friendly AI guidance' },
              { key: 'educators', icon: '🏫', label: 'AI tools for my classroom', desc: 'Practical teaching strategies' },
              { key: 'administrators', icon: '🏢', label: 'AI decisions for my school/district', desc: 'Institutional analysis & ROI' }
            ].map(option => (
              <button
                key={option.key}
                onClick={() => handleSegmentSelection(option.key)}
                style={{
                  padding: '1.5rem',
                  border: selectedSegment === option.key ? '2px solid #0EA5E9' : '2px solid #e5e7eb',
                  borderRadius: '12px',
                  background: selectedSegment === option.key ? 'rgba(14, 165, 233, 0.05)' : 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedSegment !== option.key) {
                    e.target.style.borderColor = '#94a3b8';
                    e.target.style.background = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedSegment !== option.key) {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = 'white';
                  }
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '0.5rem' }}>{option.icon}</div>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '16px', color: '#1a1a1a' }}>
                  {option.label}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {option.desc}
                </div>
              </button>
            ))}
          </div>

          {/* Pain Point Highlight */}
          <div style={{
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            border: '1px solid #F59E0B'
          }}>
            <p style={{ margin: 0, color: '#92400e', fontWeight: '500', fontSize: '16px' }}>
              ⚠️ {content.pain_point}
            </p>
          </div>

          {/* Email Signup Form */}
          <form onSubmit={handleSubmit} style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '16px',
                  fontSize: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: 'linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)',
                color: 'white',
                border: 'none',
                padding: '16px 32px',
                fontSize: '18px',
                fontWeight: '700',
                borderRadius: '8px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1,
                minWidth: '200px'
              }}
            >
              {isSubmitting ? 'Joining...' : `Get My ${content.immediate_download} →`}
            </button>
            
            <p style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginTop: '1rem',
              lineHeight: 1.4
            }}>
              📧 Weekly insights • ⏱️ 5-minute read • 🚫 No spam, unsubscribe anytime
            </p>
          </form>

          {/* Social Proof Preview */}
          <div style={{
            marginTop: '3rem',
            padding: '2rem',
            background: '#f8f9fa',
            borderRadius: '12px'
          }}>
            <h4 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#333' }}>
              What You'll Get Every Week:
            </h4>
            <div style={{ 
              display: 'grid', 
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' 
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '0.5rem' }}>🔍</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Latest AI education trends</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '0.5rem' }}>✅</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Actionable weekly checklists</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '0.5rem' }}>📊</div>
                <div style={{ fontSize: '14px', color: '#666' }}>Real data and case studies</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '3rem', color: '#666', fontSize: '14px' }}>
        <p>© 2026 Tappy Labs • Powered by Morpheus-RL Technology</p>
      </div>
    </div>
  );
};

export default NewsletterLanding;